import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { getMediaById, getRelated, getCategoryName, formatViews } from "@/utils/media";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function DetailPage() {
  const { id } = useParams();
  const media = getMediaById(id);
  const [episode, setEpisode] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentTitle(
    media ? `${media.title}详情介绍 - 91免费影视` : "影视详情 - 91免费影视",
    media ? `${media.title}：${media.summary.slice(0, 60)}` : undefined,
  );

  if (!media) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-900">影视不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的作品可能已下线或链接有误。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(media, 8);
  const favorited = isFavorite(media.id);

  const handleShare = async (channel: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {
      // 剪贴板不可用时忽略，仅作演示
    }
    setShareOpen(false);
    setToast(`${channel}分享为演示功能，页面链接已复制`);
    window.setTimeout(() => setToast(""), 2600);
  };

  const infoRows = [
    { label: "导演", value: media.director },
    { label: "主演", value: media.actors.join(" / ") },
    { label: "类型", value: media.genres.join(" / ") },
    { label: "地区", value: media.region },
    { label: "年份", value: String(media.year) },
    { label: "集数", value: media.episodes > 1 ? `${media.episodes} 集` : "电影" },
    { label: "片长", value: media.duration },
    { label: "播放热度", value: formatViews(media.views) },
  ];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${media.type}`} className="transition-colors hover:text-primary-600">
          {getCategoryName(media.type)}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="font-medium text-foreground-800">{media.title}</span>
      </nav>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <div className="w-full shrink-0 lg:w-64">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-secondary-100 ring-1 ring-background-200 sm:w-64">
            <img
              src={media.cover}
              alt={`${media.title} 影视封面`}
              title={`${media.title} - ${getCategoryName(media.type)}`}
              className="h-full w-full object-top"
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              {getCategoryName(media.type)}
            </span>
            <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
              <i className="ri-star-fill" />
              {media.rating.toFixed(1)} 分
            </span>
            <span className="text-sm text-foreground-500">{formatViews(media.views)}</span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {media.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {[String(media.year), media.region, ...media.genres, media.duration].map((tag) => (
              <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-foreground-700">
                {tag}
              </span>
            ))}
          </div>

          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {media.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="#player"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg" />
              {media.episodes > 1 ? "播放演示" : "在线观看演示"}
            </a>
            <button
              type="button"
              onClick={() => toggleFavorite(media.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors ${
                favorited
                  ? "border-accent-400 bg-accent-100 text-accent-800"
                  : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
          </div>
        </div>
      </div>

      <section id="player" className="mt-10 scroll-mt-20">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-play-circle-line text-primary-500" />
          {media.episodes > 1 ? "剧集播放（演示）" : "影片播放（演示）"}
        </h2>
        <PlayerDemo media={media} episode={episode} />

        {media.episodes > 1 && (
          <div className="mt-6">
            <EpisodeList episodes={media.episodes} current={episode} onSelect={setEpisode} />
          </div>
        )}
      </section>

      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-file-text-line text-primary-500" />
            剧情简介
          </h2>
          <div className="rounded-lg bg-background-50 p-5 ring-1 ring-background-200">
            <p className="text-sm leading-loose text-foreground-700">{media.summary}</p>
            <p className="mt-4 text-sm leading-loose text-foreground-600">
              本页面为“91免费影视”演示站点内容，影视名称、人物及评分均为虚构示例，主要用于展示详情页的排版与交互效果。
            </p>
          </div>
        </div>

        <div>
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-information-line text-primary-500" />
            详细信息
          </h2>
          <dl className="overflow-hidden rounded-lg bg-background-50 ring-1 ring-background-200">
            {infoRows.map((row, i) => (
              <div
                key={row.label}
                className={`flex gap-4 px-5 py-3 text-sm ${i % 2 === 0 ? "bg-background-50" : "bg-secondary-50"}`}
              >
                <dt className="w-20 shrink-0 text-foreground-500">{row.label}</dt>
                <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-movie-2-line text-primary-500" />
          相关推荐
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-3 no-scrollbar">
          {related.map((item) => (
            <div key={item.id} className="w-40 shrink-0 sm:w-44">
              <MediaCard media={item} />
            </div>
          ))}
        </div>
      </section>

      {shareOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-primary-950/50 px-4">
          <button
            type="button"
            aria-label="关闭分享"
            className="absolute inset-0"
            onClick={() => setShareOpen(false)}
          />
          <div className="relative w-full max-w-sm rounded-xl bg-background-50 p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-900">分享作品</h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-xl text-foreground-500 hover:bg-secondary-100"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-500">
              分享功能为前端演示，不会调用任何真实第三方接口，仅会复制当前页面链接。
            </p>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {[
                { label: "复制链接", icon: "ri-link" },
                { label: "微信", icon: "ri-wechat-line" },
                { label: "微博", icon: "ri-weibo-line" },
                { label: "更多", icon: "ri-more-line" },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleShare(item.label)}
                  className="flex flex-col items-center gap-2 rounded-lg bg-secondary-50 py-3 text-foreground-700 transition-colors hover:bg-accent-50 hover:text-accent-700"
                >
                  <i className={`${item.icon} text-xl`} />
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[80] -translate-x-1/2 rounded-md bg-primary-900 px-5 py-3 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}